import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { FaInstagram } from "react-icons/fa";
import { FaLinkedinIn } from "react-icons/fa";
import { LiaTimesSolid } from "react-icons/lia";
import { LiaBarsSolid } from "react-icons/lia";
import { AnimatePresence, motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import { gsap, ScrollTrigger } from "../../utils/gsapSetup";

const Header = () => {
    const [mobileMenu, setMobileMenu] = useState(false);
    const { t, i18n } = useTranslation();
    const headerRef = useRef(null);
    const navLinksRef = useRef([]);

    const changeLanguage = (lng) => {
        i18n.changeLanguage(lng);
    };

    useEffect(() => {
        if (!headerRef.current) return;
        // Giriş animasyonu
        gsap.fromTo(
            headerRef.current,
            { y: -40, opacity: 0 },
            { y: 0, opacity: 1, duration: 0.6, ease: "power3.out" }
        );
        // Nav link stagger
        if (navLinksRef.current.length) {
            gsap.fromTo(
                navLinksRef.current,
                { opacity: 0, y: 10 },
                { opacity: 1, y: 0, duration: 0.4, ease: "power2.out", stagger: 0.05, delay: 0.2 }
            );
        }
        // Scroll hide/show behavior
        let lastY = window.scrollY;
        const onScroll = () => {
            const currentY = window.scrollY;
            if (currentY > lastY && currentY > 120) {
                gsap.to(headerRef.current, { y: -110, duration: 0.3, ease: "power2.out" });
            } else {
                gsap.to(headerRef.current, { y: 0, duration: 0.3, ease: "power2.out" });
            }
            lastY = currentY;
        };
        window.addEventListener("scroll", onScroll, { passive: true });
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    return (
        <motion.header
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.5 }}
            className="bg-black relative z-40 h-[104px] text-[#FAFAFA] justify-between flex items-center py-8 px-8 lg:px-16"
            id="header"
            ref={headerRef}
        >
            <div className="logo flex-[1] justify-start flex">
                <Link to={"/"} className="">
                    <img src="/logonew.png" className="max-w-40 w-32 sm:w-40 h-auto" width={419} height={116} alt="Kochan Productions Logo" />
                </Link>
            </div>
            {/* desktop menu */}
            <div className="navigation flex-[2] justify-center hidden lg:flex select-none">
                <ul className="flex gap-8 font-bold text-base">
                    <li ref={(el) => (navLinksRef.current[0] = el)} onClick={() => setMobileMenu(false)} className="relative group">
                        <Link to={"/work"} className="">
                            {t("nav.photos")}
                        </Link>
                        <span className="absolute left-0 -bottom-1 h-[2px] w-0 bg-white transition-all duration-300 group-hover:w-full"></span>
                    </li>
                    <li ref={(el) => (navLinksRef.current[1] = el)} onClick={() => setMobileMenu(false)} className="relative group">
                        <Link to={"/about"} className="">
                            {t("nav.about")}
                        </Link>
                        <span className="absolute left-0 -bottom-1 h-[2px] w-0 bg-white transition-all duration-300 group-hover:w-full"></span>
                    </li>
                    <li ref={(el) => (navLinksRef.current[2] = el)} onClick={() => setMobileMenu(false)} className="relative group">
                        <Link to={"/contact"} className="">
                            {t("nav.contact")}
                        </Link>
                        <span className="absolute left-0 -bottom-1 h-[2px] w-0 bg-white transition-all duration-300 group-hover:w-full"></span>
                    </li>
                    <li ref={(el) => (navLinksRef.current[3] = el)} onClick={() => setMobileMenu(false)} className="relative group">
                        <Link to={"/social-media"} className="">
                            {t("nav.social")}
                        </Link>
                        <span className="absolute left-0 -bottom-1 h-[2px] w-0 bg-white transition-all duration-300 group-hover:w-full"></span>
                    </li>
                </ul>
            </div>

            {/* mobile menu */}

            <AnimatePresence>
                {mobileMenu && (
                    <motion.div
                        initial={{ opacity: 0, translateY: 50 }}
                        animate={{ opacity: 1, translateY: 0 }}
                        exit={{ opacity: 0, translateY: 20 }}
                        className="text-4xl fixed top-0 bg-black z-50 left-0 h-[100vh] w-full lg:hidden select-none"
                    >
                        <div className="times relative w-full">
                            <button
                                type="button"
                                aria-label={t("nav.closeMenu")}
                                className="absolute right-8 top-8"
                                onClick={() => setMobileMenu(false)}
                            >
                                <LiaTimesSolid fontSize={48} aria-hidden="true" />
                            </button>
                        </div>
                        <motion.ul
                            initial="hidden"
                            animate="show"
                            exit="hidden"
                            variants={{
                                show: { transition: { staggerChildren: 0.08, delayChildren: 0.12 } },
                                hidden: {}
                            }}
                            className="flex justify-center items-center flex-col gap-12 font-semibold h-full"
                        >
                            <motion.li
                                variants={{ hidden: { opacity: 0, y: 20 }, show: { opacity: 1, y: 0 } }}
                                onClick={() => setMobileMenu(false)}
                            >
                                <Link to={"/work"} className="">
                                    {t("nav.photos")}
                                </Link>
                            </motion.li>
                            <motion.li
                                variants={{ hidden: { opacity: 0, y: 20 }, show: { opacity: 1, y: 0 } }}
                                onClick={() => setMobileMenu(false)}
                            >
                                <Link to={"/about"} className="">
                                    {t("nav.about")}
                                </Link>
                            </motion.li>
                            <motion.li
                                variants={{ hidden: { opacity: 0, y: 20 }, show: { opacity: 1, y: 0 } }}
                                onClick={() => setMobileMenu(false)}
                            >
                                <Link to={"/contact"} className="">
                                    {t("nav.contact")}
                                </Link>
                            </motion.li>
                            <motion.li
                                variants={{ hidden: { opacity: 0, y: 20 }, show: { opacity: 1, y: 0 } }}
                                onClick={() => setMobileMenu(false)}
                            >
                                <Link to={"/social-media"} className="">
                                    {t("nav.social")}
                                </Link>
                            </motion.li>
                            <motion.li
                                variants={{ hidden: { opacity: 0, y: 20 }, show: { opacity: 1, y: 0 } }}
                                className="mt-8 flex gap-4"
                            >
                                <button onClick={() => changeLanguage("tr")} className={`px-3 py-1 border ${i18n.language.startsWith("tr") ? "border-white" : "border-[#333]"}`}>TR</button>
                                <button onClick={() => changeLanguage("en")} className={`px-3 py-1 border ${i18n.language.startsWith("en") ? "border-white" : "border-[#333]"}`}>EN</button>
                                <button onClick={() => changeLanguage("ar")} className={`px-3 py-1 border ${i18n.language.startsWith("ar") ? "border-white" : "border-[#333]"}`}>AR</button>
                            </motion.li>
                        </motion.ul>
                    </motion.div>
                )}
            </AnimatePresence>

            <div className="social gap-5 flex-[1] justify-end hidden lg:flex items-center">
                <a
                    href="https://www.instagram.com/kchnmuco/?hl=tr"
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label="Instagram"
                    className="hover:scale-110 transition-transform"
                >
                    <FaInstagram fontSize={20} aria-hidden="true" />
                </a>

                <a
                    href="https://www.linkedin.com/in/m%C3%BCcahit-ko%C3%A7han-606660308/"
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label="LinkedIn"
                >
                    <FaLinkedinIn className="hover:scale-110 transition-transform" fontSize={20} aria-hidden="true" />
                </a>
                <div className="ml-6 flex gap-2">
                    <button onClick={() => changeLanguage("tr")} className={`px-2 py-1 border ${i18n.language.startsWith("tr") ? "border-white" : "border-transparent"}`}>TR</button>
                    <button onClick={() => changeLanguage("en")} className={`px-2 py-1 border ${i18n.language.startsWith("en") ? "border-white" : "border-transparent"}`}>EN</button>
                    <button onClick={() => changeLanguage("ar")} className={`px-2 py-1 border ${i18n.language.startsWith("ar") ? "border-white" : "border-transparent"}`}>AR</button>
                </div>
            </div>
            <button
                type="button"
                className="lg:hidden"
                aria-label={t("nav.openMenu")}
                aria-expanded={mobileMenu}
                onClick={() => setMobileMenu(true)}
            >
                <LiaBarsSolid fontSize={36} aria-hidden="true" />
            </button>
        </motion.header>
    );
};

export default Header;
